import React, { createContext, useContext, useEffect, useState } from "react";
import {
  SFC_REGIONS_API,
  USER_DETAILS_API,
  CREATE_USER_API,
  UPDATE_USER_API,
  DELETE_USER_API,
  USERS_API,
  CREATE_SFC_REGION_API,
  DELETE_SFC_REGION_API,
  USERS_WITH_REGIONS_SFCS_API,
  GET_ALL_PAGE_ROLE_ACCESS_API,
  UPDATE_PAGE_ROLE_ACCESS_API,
} from "../config/apiConfig";
import { CREATE, UPDATE } from "../utils/constants";
import { securedFetch } from "../utils/api";

const AdminManagementContext = createContext();

export const AdminManagementProvider = ({ children }) => {
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [applicationAdministratorUsers, setApplicationAdministratorUsers] =
    useState([]);
  const [globalTechSupportUsers, setGlobalTechSupportUsers] = useState([]);
  const [userAccessProvisionerUsers, setUserAccessProvisionerUsers] = useState(
    [],
  );

  const [sfcRegionsLoading, setSfcRegionsLoading] = useState(true);
  const [sfcRegionsError, setSfcRegionsError] = useState(null);
  const [sfcRegions, setSfcRegions] = useState([]);

  const [usersWithRegionsAndSfcsLoading, setUsersWithRegionsAndSfcsLoading] =
    useState(true);
  const [usersWithRegionsAndSfcsError, setUsersWithRegionsAndSfcsError] =
    useState(null);
  const [usersWithRegionsAndSfcs, setUsersWithRegionsAndSfcs] = useState([]);

  const [selectedUserDetailLoading, setSelectedUserDetailLoading] =
    useState(true);
  const [selectedUserDetailError, setSelectedUserDetailError] = useState(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState({});
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);

  const [createOrUpdateUserLoading, setCreateOrUpdateUserLoading] =
    useState(true);
  const [createOrUpdateUserError, setCreateOrUpdateUserError] = useState(null);
  const [isUserCreatedOrUpdated, setIsUserCreatedOrUpdated] = useState(false);

  const [deleteUserEmail, setDeleteUserEmail] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(true);
  const [deleteUserError, setDeleteUserError] = useState(null);
  const [isUserDeleted, setIsUserDeleted] = useState(false);

  const [userFormData, setUserFormData] = useState({});

  const [createSfcRegionLoading, setCreateSfcRegionLoading] = useState(true);
  const [createSfcRegionError, setCreateSfcRegionError] = useState(null);
  const [isSfcRegionCreated, setIsSfcRegionCreated] = useState(false);

  const [deleteSfcRegion, setDeleteSfcRegion] = useState(null);
  const [deleteSfcRegionLoading, setDeleteSfcRegionLoading] = useState(true);
  const [deleteSfcRegionError, setDeleteSfcRegionError] = useState(null);
  const [isSfcRegionDeleted, setIsSfcRegionDeleted] = useState(false);

  const [sfcRegionFormData, setSfcRegionFormData] = useState({});

  const [pageRoleAccessLoading, setPageRoleAccessLoading] = useState(false);
  const [pageRoleAccessError, setPageRoleAccessError] = useState(null);
  const [pageRoleAccess, setPageRoleAccess] = useState([]);

  const [updatePageRoleAccessLoading, setUpdatePageRoleAccessLoading] =
    useState(false);
  const [updatePageRoleAccessError, setUpdatePageRoleAccessError] =
    useState(null);
  const [updatePageRoleAccessStatus, setUpdatePageRoleAccessStatus] =
    useState(false);

  const fetchUsers = () => {
    setUsersLoading(true);
    setUsersError(null);

    return securedFetch(USERS_API, {
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch List of Users");
        }
        return res.json();
      })
      .then((data) => {
        setUsers(data || []);
        const admins = data?.filter((user) => user?.admin === true);
        setAdminUsers(admins);
        const applicationAdministrators = data?.filter(
          (user) => user?.applicationAdministrator === true,
        );
        setApplicationAdministratorUsers(applicationAdministrators);
        const globalTechSupports = data?.filter(
          (user) => user?.globalTechSupport === true,
        );
        setGlobalTechSupportUsers(globalTechSupports);
        const userAccessProvisioners = data?.filter(
          (user) => user?.userAccessProvisioner === true,
        );
        setUserAccessProvisionerUsers(userAccessProvisioners);
        return data || [];
      })
      .catch((error) => {
        setUsersError(error.message);
      })
      .finally(() => {
        setUsersLoading(false);
      });
  };

  const fetchSfcRegions = () => {
    setSfcRegionsLoading(true);
    setSfcRegionsError(null);
    securedFetch(SFC_REGIONS_API, {
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch List of Regions and SFCs");
        }
        return res.json();
      })
      .then((data) => {
        setSfcRegions(data || []);
      })
      .catch((error) => {
        setSfcRegionsError(error.message);
      })
      .finally(() => {
        setSfcRegionsLoading(false);
      });
  };

  const fetchUsersWithRegionsAndSfcs = () => {
    setUsersWithRegionsAndSfcsLoading(true);
    setUsersWithRegionsAndSfcsError(null);
    securedFetch(USERS_WITH_REGIONS_SFCS_API, {
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            "Failed to fetch List of Users with Regions and SFCs",
          );
        }
        return res.json();
      })
      .then((data) => {
        setUsersWithRegionsAndSfcs(data || []);
      })
      .catch((error) => {
        setUsersWithRegionsAndSfcsError(error.message);
      })
      .finally(() => {
        setUsersWithRegionsAndSfcsLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
    fetchSfcRegions();
    fetchUsersWithRegionsAndSfcs();
    fetchAllPageRoleAccess();
  }, []);

  useEffect(() => {
    if (selectedUserEmail) {
      setSelectedUserDetailLoading(true);
      setSelectedUserDetailError(null);
      securedFetch(USER_DETAILS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: selectedUserEmail,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch List of Regions and SFCs");
          }
          return res.json();
        })
        .then((data) => {
          setSelectedUserDetail(data || []);
        })
        .catch((error) => {
          setSelectedUserDetailError(error.message);
        })
        .finally(() => {
          setSelectedUserDetailLoading(false);
        });
    }
  }, [selectedUserEmail]);

  useEffect(() => {
    if (
      userFormData !== null &&
      Object.keys(userFormData).length > 0 &&
      (userFormData?.mode === CREATE || userFormData?.mode === UPDATE)
    ) {
      setCreateOrUpdateUserLoading(true);
      setUsersLoading(true);
      setUsersWithRegionsAndSfcsLoading(true);
      setCreateOrUpdateUserError(null);
      const {
        mode,
        email,
        firstName,
        lastName,
        lanId,
        requestType,
        active,
        applicationAdministrator,
        admin,
        globalTechSupport,
        userAccessProvisioner,
        sfcPermissions,
      } = userFormData || {};
      securedFetch(mode === CREATE ? CREATE_USER_API : UPDATE_USER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          lanId,
          requestType,
          active,
          applicationAdministrator,
          admin,
          globalTechSupport,
          userAccessProvisioner,
          sfcPermissions,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to ${mode} User and their SFC Regions`);
          }
          return res.json();
        })
        .then((isCreatedOrUpdated) => {
          setIsUserCreatedOrUpdated(isCreatedOrUpdated || false);
        })
        .catch((error) => {
          setIsUserCreatedOrUpdated(false);
          setCreateOrUpdateUserError(error.message);
        })
        .finally(() => {
          setCreateOrUpdateUserLoading(false);
          fetchUsers();
          fetchUsersWithRegionsAndSfcs();
        });
    }
  }, [userFormData]);

  useEffect(() => {
    if (deleteUserEmail) {
      setDeleteUserLoading(true);
      setDeleteUserError(null);
      setUsersLoading(true);
      setUsersWithRegionsAndSfcsLoading(true);
      securedFetch(DELETE_USER_API(deleteUserEmail), {
        method: "DELETE",
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to Delete User and their SFC Regions");
          }
          return res.json();
        })
        .then((isDeleted) => {
          setIsUserDeleted(isDeleted || false);
        })
        .catch((error) => {
          setIsUserDeleted(false);
          setDeleteUserError(error.message);
        })
        .finally(() => {
          setDeleteUserLoading(false);
          fetchUsers();
          fetchUsersWithRegionsAndSfcs();
        });
    }
  }, [deleteUserEmail]);

  useEffect(() => {
    if (
      sfcRegionFormData !== null &&
      Object.keys(sfcRegionFormData).length > 0
    ) {
      setCreateSfcRegionLoading(true);
      setSfcRegionsLoading(true);
      setCreateSfcRegionError(null);
      const { regionName, businessEntityID, localCurrency, country } =
        sfcRegionFormData || {};
      securedFetch(CREATE_SFC_REGION_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          regionName,
          businessEntityID,
          localCurrency,
          country,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to Create SFC or Regions`);
          }
          return res.json();
        })
        .then((isCreated) => {
          setIsSfcRegionCreated(isCreated || false);
        })
        .catch((error) => {
          setIsSfcRegionCreated(false);
          setCreateSfcRegionError(error.message);
        })
        .finally(() => {
          setCreateSfcRegionLoading(false);
          fetchSfcRegions();
          fetchAllPageRoleAccess();
        });
    }
  }, [sfcRegionFormData]);

  useEffect(() => {
    if (deleteSfcRegion !== null && Object.keys(deleteSfcRegion).length > 0) {
      setDeleteSfcRegionLoading(true);
      setDeleteSfcRegionError(null);
      setSfcRegionsLoading(true);
      const { regionName: region, businessEntityID: sfc } =
        deleteSfcRegion || {};
      securedFetch(DELETE_SFC_REGION_API(region, sfc), {
        method: "DELETE",
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to Delete the SFC / Region");
          }
          return res.json();
        })
        .then((isDeleted) => {
          setIsSfcRegionDeleted(isDeleted || false);
        })
        .catch((error) => {
          setIsSfcRegionDeleted(false);
          setDeleteSfcRegionError(error.message);
        })
        .finally(() => {
          setDeleteSfcRegionLoading(false);
          fetchSfcRegions();
          fetchAllPageRoleAccess();
        });
    }
  }, [deleteSfcRegion]);

  const fetchAllPageRoleAccess = () => {
    setPageRoleAccessLoading(true);
    setPageRoleAccessError(null);
    securedFetch(GET_ALL_PAGE_ROLE_ACCESS_API, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch All Page Role Access");
        }
        return res.json();
      })
      .then((data) => {
        setPageRoleAccess(data || []);
      })
      .catch((error) => {
        setPageRoleAccessError(error.message);
      })
      .finally(() => {
        setPageRoleAccessLoading(false);
      });
  };

  const updatePageRoleAccess = ({ pageRoleId, pageId, access } = {}) => {
    if (pageRoleId != null && pageId != null && access != null) {
      setUpdatePageRoleAccessStatus(false);
      setUpdatePageRoleAccessLoading(true);
      setUpdatePageRoleAccessError(null);

      return securedFetch(UPDATE_PAGE_ROLE_ACCESS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pageRoleId, pageId, access }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to Update Page Role Access");
          }
          return res.json();
        })
        .then((data) => {
          setUpdatePageRoleAccessStatus(data || false);
          return data;
        })
        .catch((error) => {
          setUpdatePageRoleAccessError(error?.message);
          throw error;
        })
        .finally(() => {
          setUpdatePageRoleAccessLoading(false);
        });
    }
  };

  return (
    <AdminManagementContext.Provider
      value={{
        usersLoading,
        usersError,
        users,

        adminUsers,
        setAdminUsers,

        applicationAdministratorUsers,
        setApplicationAdministratorUsers,

        globalTechSupportUsers,
        setGlobalTechSupportUsers,

        userAccessProvisionerUsers,
        setUserAccessProvisionerUsers,

        sfcRegionsLoading,
        sfcRegionsError,
        sfcRegions,

        usersWithRegionsAndSfcsLoading,
        usersWithRegionsAndSfcsError,
        usersWithRegionsAndSfcs,

        selectedUserDetailLoading,
        selectedUserDetailError,
        selectedUserDetail,
        setSelectedUserDetail,

        selectedUserEmail,
        setSelectedUserEmail,

        createOrUpdateUserLoading,
        setCreateOrUpdateUserLoading,
        createOrUpdateUserError,
        setCreateOrUpdateUserError,
        isUserCreatedOrUpdated,
        setIsUserCreatedOrUpdated,

        deleteUserEmail,
        setDeleteUserEmail,
        deleteUserLoading,
        setDeleteUserLoading,
        deleteUserError,
        setDeleteUserError,
        isUserDeleted,
        setIsUserDeleted,

        userFormData,
        setUserFormData,

        createSfcRegionLoading,
        setCreateSfcRegionLoading,
        createSfcRegionError,
        setCreateSfcRegionError,
        isSfcRegionCreated,
        setIsSfcRegionCreated,

        sfcRegionFormData,
        setSfcRegionFormData,

        deleteSfcRegion,
        setDeleteSfcRegion,
        deleteSfcRegionLoading,
        setDeleteSfcRegionLoading,
        deleteSfcRegionError,
        setDeleteSfcRegionError,
        isSfcRegionDeleted,
        setIsSfcRegionDeleted,
        pageRoleAccessLoading,
        setPageRoleAccessLoading,
        pageRoleAccessError,
        setPageRoleAccessError,
        pageRoleAccess,
        setPageRoleAccess,
        fetchAllPageRoleAccess,

        updatePageRoleAccessLoading,
        setUpdatePageRoleAccessLoading,
        updatePageRoleAccessError,
        setUpdatePageRoleAccessError,
        updatePageRoleAccessStatus,
        setUpdatePageRoleAccessStatus,
        updatePageRoleAccess,
      }}
    >
      {children}
    </AdminManagementContext.Provider>
  );
};

export const useAdminManagementContext = () =>
  useContext(AdminManagementContext);
